import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import type { Theme } from "@mui/material";
import { alpha, Box, Checkbox, Paper, Stack, Typography } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import { differenceInMinutes, min, parseISO } from "date-fns";
import { memo, useState } from "react";
import { Link as RouterLink } from "react-router";
import { formatDate, formatDateTimeHumanized } from "@entrilia/shared/utilities/dateUtils";
import ArtifactPreviewImage from "../previews/ArtifactPreviewImage";
import type { ArtifactActionHandlers } from "./ArtifactActions";
import ArtifactActions from "./ArtifactActions";
import ArtifactFavoriteButton from "./ArtifactFavoriteButton";
import type { ArtifactRow } from "./artifactRow";
import ArtifactStatusChip from "./ArtifactStatusChip";
import { getArtifactViewUrl } from "./artifactUrls";

const tileSx = (theme: Theme): SystemStyleObject<Theme> => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  transition: theme.transitions.create(["border-color", "box-shadow"]),
  ".tile-reveal": { opacity: 0, transition: theme.transitions.create("opacity") },
  ".is-selecting & .tile-select": { opacity: 1 },
  "&:hover, &:focus-within, &.is-active": {
    borderColor: theme.palette.action.disabled,
    boxShadow: theme.shadows[2],
    ".tile-reveal": { opacity: 1 },
  },
});

const selectedTileSx = (theme: Theme): SystemStyleObject<Theme> => ({
  "&, &:hover, &:focus-within, &.is-active": {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
  },
});

const pictureSx: SystemStyleObject<Theme> = {
  position: "relative",
  borderBottom: 1,
  borderColor: "divider",
  borderRadius: "7px 7px 0 0",
  overflow: "hidden",
};

const selectedTintSx = (theme: Theme): SystemStyleObject<Theme> => ({
  position: "absolute",
  inset: 0,
  bgcolor: alpha(theme.palette.primary.main, 0.08),
});

const statusSx = (theme: Theme): SystemStyleObject<Theme> => ({
  position: "absolute",
  left: 12,
  bottom: -12,
  borderRadius: 3,
  bgcolor: "background.paper",
  boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
});

const nameSx: SystemStyleObject<Theme> = {
  mx: 1.5,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const checkboxSx: SystemStyleObject<Theme> = {
  position: "absolute",
  top: 1,
  left: 2,
  "& .MuiSvgIcon-root": { bgcolor: "background.paper", borderRadius: 1 },
};

const starSx = (theme: Theme): SystemStyleObject<Theme> => ({
  position: "absolute",
  top: 6,
  right: 6,
  bgcolor: alpha(theme.palette.background.paper, 0.92),
  boxShadow: 1,
  "&:hover": { bgcolor: "background.paper" },
});

const formatTileDate = (date: Date): string => formatDate(date, "MMM d");

const humanizeTileDate = (isoDate: string): string => {
  const now = new Date();
  const date = min([parseISO(isoDate), now]);
  return differenceInMinutes(now, date) < 1
    ? "just now"
    : formatDateTimeHumanized(date, { thresholdMinutes: 1440 }, formatTileDate);
};

interface Props extends ArtifactActionHandlers {
  artifact: ArtifactRow;
  canManage: boolean;
  isSelected: boolean;
  observerRoot?: Element;
  openedAt?: string;
  onToggleSelected: (artifactId: string) => void;
  onToggleFavorite: (artifactId: string) => void;
}

const isShallowEqual = (left: object, right: object): boolean =>
  Object.keys(left).length === Object.keys(right).length &&
  Object.entries(left).every(([key, value]) => Object.is(value, Reflect.get(right, key)));

const areTilePropsEqual = ({ artifact, ...props }: Props, { artifact: nextArtifact, ...nextProps }: Props): boolean =>
  isShallowEqual(artifact, nextArtifact) && isShallowEqual(props, nextProps);

const ArtifactTile = ({
  artifact,
  canManage,
  isSelected,
  observerRoot,
  openedAt,
  onToggleSelected,
  onToggleFavorite,
  ...actionHandlers
}: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const dateText = openedAt
    ? `Opened ${humanizeTileDate(openedAt)}`
    : `${canManage ? "Edited" : "Updated"} ${humanizeTileDate(artifact.modifiedAt ?? artifact.createdAt)}`;

  return (
    <Paper
      variant="outlined"
      className={isMenuOpen ? "is-active" : undefined}
      sx={[tileSx, isSelected && selectedTileSx]}
    >
      <Stack
        component={RouterLink}
        to={getArtifactViewUrl(artifact)}
        sx={{ flexGrow: 1, color: "inherit", textDecoration: "none" }}
      >
        <Box sx={{ position: "relative" }}>
          <Box sx={pictureSx}>
            <ArtifactPreviewImage artifact={artifact} observerRoot={observerRoot} />
            {isSelected && <Box sx={selectedTintSx} />}
          </Box>
          {canManage && (
            <Box sx={statusSx}>
              <ArtifactStatusChip artifact={artifact} />
            </Box>
          )}
        </Box>
        <Typography variant="subtitle1" sx={[nameSx, { mt: canManage ? 2.5 : 1.25 }]}>
          {artifact.name}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={0.75} useFlexGap sx={{ alignItems: "center", pl: 1.5, pr: 0.5, pt: 0.5, pb: 1 }}>
        <Typography variant="caption" color="textSecondary" noWrap>
          {dateText}
        </Typography>
        {canManage && !artifact.authorization.private && (
          <PeopleOutlinedIcon color="secondary" titleAccess="Shared" sx={{ width: 16, height: 16 }} />
        )}
        {canManage && (
          <Stack direction="row" className="tile-reveal" sx={{ ml: "auto" }}>
            <ArtifactActions
              artifact={artifact}
              {...actionHandlers}
              onMenuToggle={(artifactId) => setIsMenuOpen(artifactId !== undefined)}
            />
          </Stack>
        )}
      </Stack>
      {canManage && (
        <Checkbox
          className={isSelected ? undefined : "tile-reveal tile-select"}
          checked={isSelected}
          onChange={() => onToggleSelected(artifact.id)}
          slotProps={{ input: { "aria-label": `Select ${artifact.name}` } }}
          sx={checkboxSx}
        />
      )}
      <ArtifactFavoriteButton
        isFavorite={artifact.isFavorite}
        className={artifact.isFavorite ? undefined : "tile-reveal"}
        sx={starSx}
        onClick={() => onToggleFavorite(artifact.id)}
      />
    </Paper>
  );
};

export default memo(ArtifactTile, areTilePropsEqual);
